import { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  MenuItem,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  useGetAccountsQuery,
  useGetVoucherQuery,
  useCreateVoucherMutation,
  useUpdateVoucherMutation,
  useDeleteVoucherMutation,
} from '../../../store/api/financeApi';
import { useGetCurrentCompanyQuery } from '../../../store/api/companyApi';

const emptyLine = () => ({ accountId: '', debit: '', credit: '' });

const VOUCHER_TYPE_OPTIONS = [
  { value: 'payment', label: 'Payment Voucher' },
  { value: 'receipt', label: 'Receipt Voucher' },
  { value: 'journal', label: 'Journal Voucher' },
];

function VoucherForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isNew = id === 'new';
  const isEdit = !isNew && location.pathname.endsWith('/edit');
  const { data: voucher, isLoading } = useGetVoucherQuery(id, { skip: isNew });
  const { data: accounts = [] } = useGetAccountsQuery();
  const { data: company } = useGetCurrentCompanyQuery();
  const currency = company?.currency || 'USD';
  const [createVoucher] = useCreateVoucherMutation();
  const [updateVoucher] = useUpdateVoucherMutation();
  const [deleteVoucher] = useDeleteVoucherMutation();

  const typeFromRef = (ref) => {
    if (!ref) return 'journal';
    if (ref === 'payment_voucher') return 'payment';
    if (ref === 'receipt_voucher') return 'receipt';
    return 'journal';
  };

  const [entryDate, setEntryDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [payee, setPayee] = useState('');
  const [memo, setMemo] = useState('');
  const [voucherType, setVoucherType] = useState(() => {
    const t = searchParams.get('type');
    return (t === 'payment' || t === 'receipt' || t === 'journal') ? t : 'journal';
  });
  const [lines, setLines] = useState([emptyLine(), emptyLine()]);

  useEffect(() => {
    if (voucher) {
      setEntryDate(voucher.entryDate ? voucher.entryDate.slice(0, 10) : new Date().toISOString().split('T')[0]);
      setPayee(voucher.payee || '');
      setMemo(voucher.memo || '');
      setVoucherType(typeFromRef(voucher.referenceType));
      const l = (voucher.lines || []).map((x) => ({
        accountId: x.accountId,
        debit: x.debit != null ? String(x.debit) : '',
        credit: x.credit != null ? String(x.credit) : '',
      }));
      if (l.length >= 2) setLines(l);
      else setLines([...l, emptyLine()]);
    }
  }, [voucher]);

  const addLine = () => setLines((p) => [...p, emptyLine()]);
  const removeLine = (idx) => {
    if (lines.length <= 2) return;
    setLines((p) => p.filter((_, i) => i !== idx));
  };
  const updateLine = (idx, field, value) => {
    setLines((p) => p.map((row, i) => (i === idx ? { ...row, [field]: value } : row)));
  };

  const totalDebit = lines.reduce((s, l) => s + (parseFloat(l.debit) || 0), 0);
  const totalCredit = lines.reduce((s, l) => s + (parseFloat(l.credit) || 0), 0);
  const balanced = Math.abs(totalDebit - totalCredit) < 0.01;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!balanced) {
      alert('Total debits must equal total credits.');
      return;
    }
    const payload = {
      entryDate,
      payee: payee.trim() || undefined,
      memo: memo.trim() || undefined,
      voucherType,
      lines: lines
        .filter((l) => l.accountId && (parseFloat(l.debit) || parseFloat(l.credit)))
        .map((l) => ({
          accountId: l.accountId,
          debit: parseFloat(l.debit) || 0,
          credit: parseFloat(l.credit) || 0,
        })),
    };
    if (payload.lines.length < 2) {
      alert('Add at least two lines with account and amount.');
      return;
    }
    try {
      if (isNew) {
        await createVoucher(payload).unwrap();
        navigate('/finance/vouchers');
      } else {
        await updateVoucher({ id, ...payload }).unwrap();
        navigate('/finance/vouchers');
      }
    } catch (err) {
      alert(err?.data?.message || 'Error saving voucher');
    }
  };

  const handleDelete = async () => {
    if (!confirm('Delete this voucher?')) return;
    try {
      await deleteVoucher(id).unwrap();
      navigate('/finance/vouchers');
    } catch (err) {
      alert(err?.data?.message || 'Error deleting');
    }
  };

  if (!isNew && isLoading) return <Typography>Loading...</Typography>;

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/finance/vouchers')} sx={{ mb: 2 }}>
        Back
      </Button>
      <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 3 }}>
        {isNew
          ? `New ${VOUCHER_TYPE_OPTIONS.find((o) => o.value === voucherType)?.label || 'Voucher'}`
          : isEdit
            ? 'Edit Voucher'
            : (VOUCHER_TYPE_OPTIONS.find((o) => o.value === voucherType)?.label || 'Voucher')}
      </Typography>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              select
              label="Voucher type"
              value={voucherType}
              onChange={(e) => setVoucherType(e.target.value)}
              sx={{ mr: 2, mb: 2, minWidth: 180 }}
            >
              {VOUCHER_TYPE_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </TextField>
            <TextField
              type="date"
              label="Date"
              value={entryDate}
              onChange={(e) => setEntryDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              required
              sx={{ mr: 2, mb: 2 }}
            />
            <TextField
              label="Payee"
              value={payee}
              onChange={(e) => setPayee(e.target.value)}
              placeholder="Name or party for this voucher"
              sx={{ mr: 2, mb: 2, minWidth: 220 }}
            />
            <TextField
              label="Note"
              value={memo}
              onChange={(e) => setMemo(e.target.value)}
              placeholder="Optional description or reference"
              sx={{ minWidth: 280, mb: 2 }}
            />
            <Table size="small" sx={{ mt: 2 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Account</TableCell>
                  <TableCell align="right">Debit</TableCell>
                  <TableCell align="right">Credit</TableCell>
                  <TableCell width={60} />
                </TableRow>
              </TableHead>
              <TableBody>
                {lines.map((row, idx) => (
                  <TableRow key={idx}>
                    <TableCell>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        value={row.accountId}
                        onChange={(e) => updateLine(idx, 'accountId', e.target.value)}
                        sx={{ minWidth: 220 }}
                      >
                        <MenuItem value="">Select account</MenuItem>
                        {accounts.map((a) => (
                          <MenuItem key={a.id} value={a.id}>
                            {a.code} – {a.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    </TableCell>
                    <TableCell align="right">
                      <TextField
                        type="number"
                        size="small"
                        inputProps={{ step: 0.01, min: 0 }}
                        value={row.debit}
                        onChange={(e) => {
                          updateLine(idx, 'debit', e.target.value);
                          if (row.credit) updateLine(idx, 'credit', '');
                        }}
                        sx={{ width: 120 }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <TextField
                        type="number"
                        size="small"
                        inputProps={{ step: 0.01, min: 0 }}
                        value={row.credit}
                        onChange={(e) => {
                          updateLine(idx, 'credit', e.target.value);
                          if (row.debit) updateLine(idx, 'debit', '');
                        }}
                        sx={{ width: 120 }}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => removeLine(idx)} disabled={lines.length <= 2}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Button startIcon={<AddIcon />} onClick={addLine} sx={{ mt: 1 }}>
              Add line
            </Button>
            <Box sx={{ mt: 2 }}>
              <Typography color={balanced ? 'text.secondary' : 'error.main'}>
                Total Debit: {currency} {totalDebit.toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2))} · Total Credit: {currency} {totalCredit.toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2))}
                {!balanced && ' (must match)'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, mt: 3 }}>
              {(isNew || isEdit) && (
                <Button type="submit" variant="contained" disabled={!balanced}>
                  {isNew ? 'Create' : 'Save'}
                </Button>
              )}
              {!isNew && isEdit && (
                <Button color="error" onClick={handleDelete}>
                  Delete
                </Button>
              )}
              {!isNew && !isEdit && (
                <Button variant="contained" onClick={() => navigate(`/finance/vouchers/${id}/edit`)}>
                  Edit
                </Button>
              )}
              <Button onClick={() => navigate('/finance/vouchers')}>Cancel</Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}

export default VoucherForm;
